import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import { ROUTES } from '../constants/routes';
import { useCart } from '../context/CartContext';
import { useCreateTicketMutation } from '../store/supportApi';
import {
  GROUPS, CONCERNS, buildPackage, PACKAGE_DISCOUNT, DISCOUNT_FROM,
} from '../data/concernPackages';

// Build your own package — the reference's four-step builder.
//
// Steps: concerns → your tests → about you → doctor review.
//
// Everything above step 3 is the reference. Step 3 is not: the reference
// ships a prototype tracker with a hardcoded "Request #BYP-1042", five
// fake stages and a button that advances them by hand. There is no
// doctor-review workflow in the API — no reviewer, no request record, no
// status — so a tracker here would be showing a patient a progress bar
// over nothing. Instead the request is filed as a real support ticket
// with the whole package in it, exactly as Doctor Consult does, and the
// page also offers the path that does work today: put these tests in the
// cart and book a slot. API-NOTES 2.40.
//
// The tests themselves are real catalogue records — see the note at the
// top of data/concernPackages.js for how each was resolved and why the
// obvious match was often wrong.

const STEP_LABELS = ['Concerns', 'Your tests', 'About you', 'Doctor review', 'Collection'];
const GENDERS = ['Female', 'Male', 'Other'];

const panel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const stepTitle = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(28px, 3.4vw, 40px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

const field = {
  border: '1.5px solid var(--tl-line)',
  borderRadius: '14px',
  outline: 0,
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  p: '13px 14px',
  color: 'var(--tl-g9)',
  bgcolor: '#FFFFFF',
  width: '100%',
  minWidth: 0,
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const fieldLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  minWidth: 0,
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--tl-muted-2)',
};

const cta = {
  border: 0,
  borderRadius: '16px',
  p: '15px 22px',
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  fontWeight: 800,
  textAlign: 'center',
  textDecoration: 'none',
  cursor: 'pointer',
};

const inr = (n) => Number(n || 0).toLocaleString('en-IN');

const BuildPackage = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [createTicket, { isLoading: sending }] = useCreateTicketMutation();

  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('Female');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const all = useMemo(() => buildPackage(selected, gender), [selected, gender]);
  const active = useMemo(() => all.filter((t) => !removed.includes(t.k)), [all, removed]);

  const subtotal = active.reduce((a, t) => a + (t.price || 0), 0);
  const hasDiscount = active.length >= DISCOUNT_FROM;
  const discount = hasDiscount ? Math.round(subtotal * PACKAGE_DISCOUNT) : 0;
  const total = subtotal - discount;
  const needsFasting = active.some((t) => t.fasting);

  const concernNames = selected.map((k) => CONCERNS[k]?.l).filter(Boolean);

  const canAdvance = step === 0
    ? selected.length > 0
    : step === 1
      ? active.length > 0
      : step === 2
        ? Boolean(name.trim()) && phone.replace(/\D/g, '').length >= 10
        : true;

  const toggleConcern = (k) =>
    setSelected((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  const toggleTest = (k) =>
    setRemoved((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  const next = () => {
    if (!canAdvance) return;
    setError('');
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setError('');
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // The path that works today: real tests into the real cart, then the
  // existing booking flow prices and schedules them.
  const bookNow = () => {
    active.forEach((t) => addItem({
      id: String(t.id),
      type: 'test',
      name: t.name,
      price: t.price,
      fasting: Boolean(t.fasting),
    }));
    navigate(ROUTES.BOOKING);
  };

  const sendForReview = async () => {
    setError('');
    try {
      await createTicket({
        name: name.trim(),
        phone: phone.trim(),
        category: 'other',
        priority: 'normal',
        subject: `Build-your-own package — ${active.length} tests, doctor review requested`,
        message: [
          `Concerns: ${concernNames.join(', ') || 'not chosen'}`,
          `Age: ${age.trim() || 'not given'} · Gender: ${gender}`,
          notes.trim() ? `Medicines / conditions: ${notes.trim()}` : null,
          '',
          'Tests selected:',
          ...active.map((t) => `· ${t.name} (id ${t.id}) — ₹${t.price} — for ${t.why.join(', ')}`),
          '',
          `Estimated total: ₹${total}${hasDiscount ? ` (after ${Math.round(PACKAGE_DISCOUNT * 100)}% package saving)` : ''}`,
        ].filter((l) => l !== null).join('\n'),
      }).unwrap();
      setSent(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.data?.message || 'Could not send your package for review — please try again.');
    }
  };

  return (
    <Box
      component="main"
      sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 2, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      {/* ── hero + stepper ───────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.25,
          p: 'clamp(24px, 4vw, 44px)',
          borderRadius: '32px',
          bgcolor: 'var(--tl-teal)',
          color: '#FFFFFF',
        }}
      >
        <Box
          component="span"
          sx={{
            alignSelf: 'start',
            p: '6px 10px',
            borderRadius: '999px',
            bgcolor: 'var(--tl-yellow)',
            color: 'var(--tl-g9)',
            fontFamily: UI,
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          Build your own package
        </Box>
        <Typography
          component="h1"
          sx={{
            m: 0,
            fontFamily: DISPLAY,
            fontWeight: 700,
            fontSize: 'clamp(32px, 5vw, 64px)',
            lineHeight: 0.98,
            letterSpacing: '-.035em',
            textWrap: 'balance',
            color: '#FFFFFF',
          }}
        >
          Start with what worries you.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 0.75 }}>
          {STEP_LABELS.map((l, i) => {
            const on = i <= step;
            return (
              <Box key={l} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                <Box sx={{ height: 6, borderRadius: '999px', bgcolor: on ? '#FFFFFF' : 'rgba(255,255,255,0.3)' }} />
                <Box
                  component="span"
                  sx={{
                    fontFamily: UI,
                    fontSize: 13,
                    fontWeight: 800,
                    color: on ? '#FFFFFF' : 'rgba(255,255,255,0.65)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {l}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ flex: '2 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* ── step 0 — concerns ──────────────────────────────────── */}
          {step === 0 && (
            <>
              <Box
                component={Link}
                to={ROUTES.DOCTOR_CONSULT}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  p: '16px 20px',
                  borderRadius: '22px',
                  bgcolor: '#FFF7C2',
                  color: 'var(--tl-g9)',
                  textDecoration: 'none',
                }}
              >
                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 16 }}>
                    Not sure where to start?
                  </Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: '#3A4020' }}>
                    Talk to a lab doctor for 15 minutes. The fee is adjusted when you book.
                  </Box>
                </Box>
                <Box
                  component="span"
                  sx={{ p: '10px 16px', borderRadius: '999px', bgcolor: 'var(--tl-g9)', color: '#FFFFFF', fontFamily: UI, fontWeight: 800, fontSize: 14, whiteSpace: 'nowrap' }}
                >
                  Book a consult
                </Box>
              </Box>

              {GROUPS.map((g) => (
                <Box key={g.t} component="section" sx={{ ...panel, gap: 1.75 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, color: 'var(--tl-g9)' }}>
                      {g.t}
                    </Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>{g.s}</Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 170px), 1fr))', gap: 1 }}>
                    {g.items.map((item) => {
                      const on = selected.includes(item.k);
                      return (
                        <Box
                          key={item.k}
                          component="button"
                          type="button"
                          onClick={() => toggleConcern(item.k)}
                          aria-pressed={on}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            minHeight: 76,
                            textAlign: 'left',
                            cursor: 'pointer',
                            p: 1.75,
                            borderRadius: '18px',
                            font: 'inherit',
                            border: `2px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                            bgcolor: on ? 'var(--tl-teal)' : '#FFFFFF',
                            color: on ? '#FFFFFF' : 'var(--tl-g9)',
                          }}
                        >
                          <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 0.75 }}>
                            <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>
                              {item.l}
                            </Box>
                            <Box component="span" aria-hidden="true" sx={{ fontWeight: 800, fontSize: 15 }}>{on ? '✓' : '+'}</Box>
                          </Box>
                          <Box component="span" sx={{ fontFamily: UI, fontSize: 12, opacity: 0.8 }}>
                            {item.tests.length} tests
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </>
          )}

          {/* ── step 1 — your tests ────────────────────────────────── */}
          {step === 1 && (
            <Box component="section" sx={panel}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography component="h2" sx={stepTitle}>Your package</Typography>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted-2)' }}>
                  Built from: {concernNames.join(', ')}. Remove anything you don&rsquo;t want; our
                  doctor will check the final list.
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {all.map((t) => {
                  const off = removed.includes(t.k);
                  return (
                    <Box
                      key={t.k}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        py: 1.75,
                        borderTop: '1px solid #EEF3F1',
                        opacity: off ? 0.45 : 1,
                      }}
                    >
                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0 }}>
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Box
                            component="span"
                            sx={{ fontFamily: UI, fontWeight: 800, fontSize: 16, color: 'var(--tl-g9)', textDecoration: off ? 'line-through' : 'none' }}
                          >
                            {t.name}
                          </Box>
                          {t.fasting && (
                            <Box component="span" sx={{ p: '3px 8px', borderRadius: '999px', bgcolor: '#FFF4CC', fontFamily: UI, fontSize: 11, fontWeight: 800, color: 'var(--tl-g9)' }}>
                              Fasting
                            </Box>
                          )}
                        </Box>
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                          For {t.why.join(', ')}
                        </Box>
                      </Box>
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                        <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, color: 'var(--tl-g9)' }}>₹ {inr(t.price)}</Box>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => toggleTest(t.k)}
                          sx={{ border: 0, cursor: 'pointer', borderRadius: '999px', p: '8px 12px', font: 'inherit', fontFamily: UI, fontSize: 13, fontWeight: 800, bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)' }}
                        >
                          {off ? 'Add back' : 'Remove'}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── step 2 — about you ─────────────────────────────────── */}
          {step === 2 && (
            <Box component="section" sx={{ ...panel, gap: 2.25 }}>
              <Typography component="h2" sx={stepTitle}>Tell the doctor about you</Typography>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: 'var(--tl-muted-2)' }}>
                This helps our doctor check that the package is right for you. It stays private.
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.25 }}>
                <Box component="label" sx={fieldLabel}>
                  Full name
                  <Box component="input" value={name} onChange={(e) => setName(e.target.value)} sx={field} />
                </Box>
                <Box component="label" sx={fieldLabel}>
                  Mobile (WhatsApp)
                  <Box component="input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="+91" sx={field} />
                </Box>
                <Box component="label" sx={fieldLabel}>
                  Age
                  <Box component="input" value={age} onChange={(e) => setAge(e.target.value)} inputMode="numeric" sx={field} />
                </Box>
                <Box sx={fieldLabel}>
                  Gender
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    {GENDERS.map((g) => {
                      const on = g === gender;
                      return (
                        <Box
                          key={g}
                          component="button"
                          type="button"
                          onClick={() => setGender(g)}
                          aria-pressed={on}
                          sx={{
                            flex: 1,
                            cursor: 'pointer',
                            borderRadius: '14px',
                            p: '12px 6px',
                            font: 'inherit',
                            fontFamily: UI,
                            fontSize: 14,
                            fontWeight: 800,
                            border: `1.5px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                            bgcolor: on ? 'var(--tl-mint)' : '#FFFFFF',
                            color: 'var(--tl-g9)',
                          }}
                        >
                          {g}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
              <Box component="label" sx={fieldLabel}>
                Current medicines or conditions (optional)
                <Box
                  component="textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="e.g. on metformin for 2 years, mother has thyroid"
                  sx={{ ...field, resize: 'vertical', lineHeight: 1.6 }}
                />
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                Changing gender may add or remove a test — PSA is dropped for women, and LH/FSH
                and prolactin for men unless you picked Hormones.
              </Box>
            </Box>
          )}

          {/* ── step 3 — doctor review ─────────────────────────────── */}
          {step === 3 && (
            <Box component="section" sx={{ ...panel, gap: 2.25 }}>
              {sent ? (
                <>
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'var(--tl-teal)', color: '#FFFFFF', display: 'grid', placeItems: 'center', fontSize: 26, fontWeight: 800 }}
                  >
                    ✓
                  </Box>
                  <Typography component="h2" sx={stepTitle}>
                    Sent for review, {name.trim().split(' ')[0] || 'there'}.
                  </Typography>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
                    A TrustLab doctor will check your {active.length}-test list and message{' '}
                    {phone.trim()} on WhatsApp. They may add or drop a test — the price is
                    confirmed then.
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Box component={Link} to={ROUTES.MY_TICKETS} sx={{ ...cta, bgcolor: 'var(--tl-g9)', color: '#FFFFFF' }}>
                      Track this request
                    </Box>
                    <Box component="button" type="button" onClick={bookNow} sx={{ ...cta, bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)' }}>
                      Book these tests now
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <Typography component="h2" sx={stepTitle}>Ready when you are</Typography>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted-2)' }}>
                    {active.length} tests for {concernNames.join(', ')}. You can have a doctor
                    check the list first, or book it straight away.
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '16px 18px', borderRadius: '18px', bgcolor: '#F6FAF8' }}>
                    <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 15, color: 'var(--tl-g9)' }}>
                      Send for doctor review
                    </Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: 'var(--tl-muted)' }}>
                      A pathologist checks your list against your age, sex and what you told us,
                      then messages you on WhatsApp. Usually within a working day.
                    </Box>
                  </Box>

                  {error && (
                    <Box component="span" role="alert" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: '#B23B37' }}>
                      {error}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={sendForReview}
                      disabled={sending}
                      sx={{ ...cta, bgcolor: sending ? '#B9C9C4' : 'var(--tl-g9)', color: '#FFFFFF', cursor: sending ? 'default' : 'pointer' }}
                    >
                      {sending ? 'Sending…' : 'Send for doctor review'}
                    </Box>
                    <Box component="button" type="button" onClick={bookNow} sx={{ ...cta, bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)' }}>
                      Book without review →
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* ── back / next ────────────────────────────────────────── */}
          {!sent && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25 }}>
              <Box
                component="button"
                type="button"
                onClick={back}
                sx={{ ...cta, bgcolor: '#FFFFFF', color: 'var(--tl-g9)', visibility: step === 0 ? 'hidden' : 'visible' }}
              >
                ← Back
              </Box>
              {step < 3 && (
                <Box
                  component="button"
                  type="button"
                  onClick={next}
                  disabled={!canAdvance}
                  sx={{
                    ...cta,
                    flex: 1,
                    maxWidth: 380,
                    cursor: canAdvance ? 'pointer' : 'default',
                    bgcolor: canAdvance ? 'var(--tl-g9)' : 'rgba(11,43,39,0.35)',
                    color: '#FFFFFF',
                  }}
                >
                  {step === 0
                    ? (selected.length ? `See my ${all.length} tests →` : 'Pick a concern to start')
                    : step === 1 ? 'Looks right →' : 'Almost done →'}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* ── sticky summary ───────────────────────────────────────── */}
        <Box
          component="aside"
          sx={{
            flex: '1 1 320px',
            minWidth: 0,
            position: 'sticky',
            top: 100,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.75,
            p: 3,
            borderRadius: '28px',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
          }}
        >
          <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, color: '#FFFFFF' }}>
            Your package
          </Box>

          {selected.length === 0 && (
            <Box component="span" sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: '#9FC2BA' }}>
              Pick one or more concerns. We&rsquo;ll add the right tests here.
            </Box>
          )}

          {concernNames.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {concernNames.map((c) => (
                <Box
                  key={c}
                  component="span"
                  sx={{ p: '6px 10px', borderRadius: '999px', bgcolor: 'var(--tl-g7)', color: 'var(--tl-teal-l)', fontFamily: UI, fontSize: 12, fontWeight: 800 }}
                >
                  {c}
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.5, borderTop: '1px solid var(--tl-g6)', fontFamily: UI, fontSize: 14 }}>
            <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box component="span" sx={{ color: '#9FC2BA' }}>
                {active.length} {active.length === 1 ? 'test' : 'tests'}
              </Box>
              <Box component="span" sx={{ fontWeight: 800 }}>₹ {inr(subtotal)}</Box>
            </Box>
            {hasDiscount && (
              <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Box component="span" sx={{ color: '#9FC2BA' }}>Package saving ({Math.round(PACKAGE_DISCOUNT * 100)}%)</Box>
                <Box component="span" sx={{ fontWeight: 800, color: 'var(--tl-teal-l)' }}>− ₹ {inr(discount)}</Box>
              </Box>
            )}
            <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box component="span" sx={{ color: '#9FC2BA' }}>Doctor review</Box>
              <Box component="span" sx={{ fontWeight: 800, color: 'var(--tl-teal-l)' }}>Included</Box>
            </Box>
            <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box component="span" sx={{ color: '#9FC2BA' }}>Home collection</Box>
              <Box component="span" sx={{ fontWeight: 800, color: 'var(--tl-teal-l)' }}>Free</Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: 1.75, borderTop: '1px solid var(--tl-g6)' }}>
            <Box component="span" sx={{ fontFamily: UI, fontWeight: 800 }}>Total</Box>
            <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 32 }}>₹ {inr(total)}</Box>
          </Box>

          {needsFasting && (
            <Box
              component="span"
              sx={{ p: '10px 12px', borderRadius: '14px', bgcolor: 'var(--tl-g7)', fontFamily: UI, fontSize: 13, lineHeight: 1.5, color: 'var(--tl-yellow)' }}
            >
              Some tests need 10–12 hours fasting. We&rsquo;ll suggest an early-morning slot.
            </Box>
          )}

          <Box component="span" sx={{ fontFamily: UI, fontSize: 12, lineHeight: 1.5, color: '#9FC2BA' }}>
            An estimate from our current catalogue. The final price is confirmed after doctor
            review.
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BuildPackage;
